import React, { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { CivSelector } from '../../components/CivSelector'
import { MapSelector } from '../../components/MapSelector'
import { mergeCivDraftView, mergeMapDraftView } from '../../helpers/mergeDraftView'
import { useSocketState } from '../../helpers/useSocket'
import { translate } from '../../helpers/i18n'
import Countdown from 'react-countdown'
import { countdownRenderer } from '../../helpers/countdownRenderer'
import { useQuery } from 'react-query'
import { getBackendUrl } from '../../helpers/url'
import { FaChevronDown, FaExternalLinkAlt } from 'react-icons/fa'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DragHandle } from './ScheduleDashboard'

// aoe2cm draft/preset ids can contain underscores and hyphens, so take the last
// path segment instead of matching a bare alphanumeric tail
function extractAoe2cmId(url) {
	return url
		?.trim()
		.replace(/[?#].*$/, '')
		.replace(/\/+$/, '')
		.match(/[a-zA-Z0-9_-]+$/)
		?.at(0)
}

// games carry no id of their own, so sortable ids are derived from their position
const gameSortId = (gameIndex) => `game-${gameIndex}`

function SortableGame({ id, gameNumber, children }) {
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	}

	return (
		<div ref={setNodeRef} style={style} className="flex flex-row pt-4">
			<div className="self-center flex flex-col items-center">
				<span className="text-xs text-gray-400">Game</span>
				<span className="text-lg font-bold leading-none">{gameNumber}</span>
				<DragHandle listeners={listeners} attributes={attributes} />
			</div>
			{children}
		</div>
	)
}

export const MATCH_TITLE_PRESETS = [
	{ id: 'showmatch', label: 'Showmatch', translations: { fr: "Match d'exhibition", no: 'Oppvisningskamp' } },
	{ id: 'semifinal', label: 'Semi Final', translations: { fr: 'Demi-finale', no: 'Semifinale' } },
	{ id: 'grandfinal', label: 'Grand Final', translations: { fr: 'Grande finale', no: 'Finale' } },
]

export function findMatchTitlePreset(matchTitle) {
	const title = matchTitle?.trim().toLowerCase()
	if (!title) return null
	return MATCH_TITLE_PRESETS.find((preset) => preset.label.toLowerCase() === title) ?? null
}

export function matchTitleLabel(matchTitle, lang) {
	if (!matchTitle) return ''
	const preset = findMatchTitlePreset(matchTitle)
	if (!preset) return matchTitle
	return translate(preset.translations, preset.label, lang)
}

function DraftDashboard() {
	const { socket } = useOutletContext()
	const [match] = useSocketState('Match')
	const [tournament] = useSocketState('Tournament')
	const [gameCivDraft] = useSocketState('GameCivDraft')
	const [civDraft] = useSocketState('CivDraft')
	const [mapDraft] = useSocketState('MapDraft')
	const [preset] = useSocketState('MapDraft')
	const [gameCivDraftUrl, setGameCivDraftUrl] = useState(null)
	const [civDraftUrl, setCivDraftUrl] = useState(null)
	const [mapDraftUrl, setMapDraftUrl] = useState(null)
	const [presetUrl, setPresetUrl] = useState(null)
	const [customMinutes, setCustomMinutes] = useState(0)
	const [customSeconds, setCustomSeconds] = useState(0)
	const [matchTitle, setMatchTitle] = useState('')

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

	// keep the title field in sync when the match is changed elsewhere
	useEffect(() => {
		setMatchTitle(match?.matchTitle ?? '')
	}, [match?.matchTitle])

	// Delay load draftId
	if (gameCivDraftUrl === null && gameCivDraft?.draftId) {
		setGameCivDraftUrl(gameCivDraft.draftId)
	}
	if (civDraftUrl === null && civDraft?.draftId) {
		setCivDraftUrl(civDraft.draftId)
	}
	if (mapDraftUrl === null && mapDraft?.draftId) {
		setMapDraftUrl(mapDraft.draftId)
	}
	if (presetUrl === null && preset?.draftId) {
		setPresetUrl(civDraft.httpPresetId)
	}

	const fetchMaps = async () => {
		const res = await fetch(`${getBackendUrl()}/api/config/maps`)
		return res.json()
	}

	const { data: maps } = useQuery('maps', fetchMaps)

	const fetchMatchTypes = async () => {
		const res = await fetch(`${getBackendUrl()}/api/config/match-types`)
		return res.json()
	}

	const { data: matchTypes } = useQuery('matchTypes', fetchMatchTypes)

	const lockedMaps = tournament?.config?.settings?.lockedMaps
	const usePhysicalDraft = tournament?.config?.settings?.usePhysicalDraft
	const effectiveMaps = lockedMaps && tournament?.config?.maps?.length ? maps?.filter((m) => tournament.config.maps.includes(m.label)) : maps

	const gameCivDraftView = gameCivDraft ? mergeCivDraftView(gameCivDraft?.view || {}, [], 0, false) : {}
	const civDraftView = civDraft && match ? mergeCivDraftView(civDraft?.view || {}, match.games, match.matchRedraftAfter, true) : {}
	const mapDraftView = mapDraft && match ? mergeMapDraftView(mapDraft?.view || {}, match.games, 0, true) : {}

	const filterCivs = match?.filterCivs
	const leftAvailableCivs = filterCivs && civDraftView?.host ? civDraftView.host.filter((d) => d.action === 'pick').map((d) => d.option) : null
	const rightAvailableCivs = filterCivs && civDraftView?.guest ? civDraftView.guest.filter((d) => d.action === 'pick').map((d) => d.option) : null

	const gameOptions = matchTypes ?? []
	const gameStateOptions = ['left', 'right', 'upcoming', 'tbd', 'picked']
	const statusPresets = [
		{ id: 'drafting', value: 'Players are drafting' },
		{ id: 'beginning', value: 'Game is about to begin...' },
		{ id: 'finished', value: 'Thank you for your support! ♥' },
		{ id: 'interview', value: 'Interview is about to begin...' },
		{ id: 'lobby', value: 'Lobby is getting ready' },
		{ id: 'hide', value: '' },
	]

	const expectedNumGames = match?.matchTypeLength ?? 0
	const hasRedraft = expectedNumGames >= 7

	function handleImportGameCivDraft() {
		const draftId = extractAoe2cmId(gameCivDraftUrl)
		socket.emit('setGameCivDraftId', draftId)
	}

	function handleImportPreset() {
		const presetId = extractAoe2cmId(presetUrl)
		socket.emit('setCivDraftPreset', presetId)
	}

	function handleImportCivDraft() {
		const draftId = extractAoe2cmId(civDraftUrl)
		socket.emit('setCivDraftId', draftId)
		if (draftId) {
			// Auto set redraft, just for this tourney
			if (hasRedraft) {
				const nextGameIndex = match.games.findIndex((v) => v.state === 'tbd' || v.state === 'upcoming')
				if (nextGameIndex === 4) {
					socket.emit('modifyMatch', { matchRedraftAfter: 4 })
				} else if (nextGameIndex === 0) {
					socket.emit('modifyMatch', { matchRedraftAfter: 0 })
				}
			} else if (match.matchRedraftAfter !== 0) {
				socket.emit('modifyMatch', { matchRedraftAfter: 0 })
			}
		}
	}

	function handleChangeCiv(el, index, side) {
		console.log(el, index, side)

		let newDraftOption = { ...civDraft.view[side][index] }
		newDraftOption.option = el

		let newDraft = [...civDraft.view[side]]

		newDraft[index] = { ...newDraft[index], ...newDraftOption }

		socket.emit(`modifyCiv${side}Draft`, newDraft)
	}

	function handleChangeMap(el, index, side) {
		let newDraftOption = { ...mapDraft.view[side][index] }
		newDraftOption.option = el

		let newDraft = [...mapDraft.view[side]]

		newDraft[index] = { ...newDraft[index], ...newDraftOption }

		socket.emit(`modifyMap${side}Draft`, newDraft)
	}

	function handleImportMapDraft() {
		const draftId = extractAoe2cmId(mapDraftUrl)
		socket.emit('setMapDraftId', draftId)
	}

	function handleSwapGameCivDraftHost() {
		socket.emit('swapGameCivDraftPlayers')
	}

	function handleSwapCivDraftHost() {
		socket.emit('swapCivDraftPlayers')
	}

	function handleSwapMapDraftHost() {
		socket.emit('swapMapDraftPlayers')
	}

	function handleGameCivDraftUrlChange(el) {
		setGameCivDraftUrl(el.target.value)
	}

	function handleCivDraftUrlChange(el) {
		setCivDraftUrl(el.target.value)
	}

	function handlePresetUrlChange(el) {
		setPresetUrl(el.target.value)
	}

	function handleMapDraftUrlChange(el) {
		setMapDraftUrl(el.target.value)
	}

	function handleMatchTypeChange(el) {
		socket.emit('modifyMatch', { matchType: el.target.value })
	}

	function handleMatchRedraftAfterChange(el) {
		socket.emit('modifyMatch', { matchRedraftAfter: el.target.value })
	}

	function modifyGame(gameIndex, patch) {
		let newGames = [...match.games]
		newGames[gameIndex] = { ...newGames[gameIndex], ...patch }
		socket.emit('modifyMatch', { games: newGames })
	}

	function handleGameDragEnd(event) {
		const { active, over } = event
		if (!over || active.id === over.id) return

		const gameIds = match.games.map((_, i) => gameSortId(i))
		const oldIndex = gameIds.indexOf(active.id)
		const newIndex = gameIds.indexOf(over.id)
		// remember where each game started, so the display can keep that order when reordering is disabled
		const games = match.games.map((game, i) => ({ ...game, originalIndex: game.originalIndex ?? i }))
		socket.emit('modifyMatch', { games: arrayMove(games, oldIndex, newIndex) })
	}

	function _modifyGameDraft(gameIndex, patch) {
		let newGames = [...match.games]
		newGames[gameIndex] = { ...newGames[gameIndex], ...patch }
		socket.emit('modifyMatch', { games: newGames })
	}

	function getFutureTimestamp(seconds) {
		if (seconds === null) {
			socket.emit('modifyMatch', { countdownTimer: null })
			return
		}
		var date = new Date().getTime() + seconds * 1000
		socket.emit('modifyMatch', { countdownTimer: null })

		socket.emit('modifyMatch', { countdownTimer: date })
	}

	function setCustomTimer() {
		const totalSeconds = Number(customMinutes) * 60 + Number(customSeconds)
		if (totalSeconds > 0) {
			getFutureTimestamp(totalSeconds)
		}
	}

	function adjustTimer(deltaSeconds) {
		if (!match?.countdownTimer) return
		const adjusted = match.countdownTimer + deltaSeconds * 1000
		socket.emit('modifyMatch', { countdownTimer: null })
		socket.emit('modifyMatch', { countdownTimer: adjusted })
	}

	function handleMatchStatusChange(el) {
		const text = statusPresets.find((item) => item.id === el.target.value)
		socket.emit('modifyMatch', { matchStatus: text.value })
	}

	function handleRoundInfo(el) {
		el.preventDefault()
		socket.emit('modifyMatch', {
			roundInfo: el.target.roundInfo.value,
			matchTitle,
		})
	}

	function handleMatchTitlePresetChange(el) {
		const preset = MATCH_TITLE_PRESETS.find((item) => item.id === el.target.value)
		setMatchTitle(preset ? preset.label : '')
	}

	function copyNamesFromGameCivDraft() {
		socket.emit('modifyLeftPlayer', { name: gameCivDraftView.hostName })
		socket.emit('modifyRightPlayer', { name: gameCivDraftView.guestName })
	}

	function copyNamesFromCivDraft() {
		socket.emit('modifyLeftPlayer', { name: civDraftView.hostName })
		socket.emit('modifyRightPlayer', { name: civDraftView.guestName })
	}

	function copyNamesFromMapDraft() {
		socket.emit('modifyLeftPlayer', { name: mapDraftView.hostName })
		socket.emit('modifyRightPlayer', { name: mapDraftView.guestName })
	}

	function convertFromMapDraft() {
		// converting restarts the order from the map draft
		let newGames = match.games.map((game, i) => ({ ...game, originalIndex: i }))

		let pickedMaps = [...mapDraftView.host, ...mapDraftView.admin, ...mapDraftView.guest].filter((pickedMap) => pickedMap.action === 'pick')
		console.log(pickedMaps)
		pickedMaps.forEach((pickedMap, i) => {
			if (newGames[i]) newGames[i].map = pickedMap.option
		})
		socket.emit('modifyMatch', { games: newGames })
	}

	function setAllMapsPicked() {
		let newGames = [...match.games]

		// set any game with state 'tbd' to 'picked'
		newGames = newGames.map((game) => (game && game.state === 'tbd' ? { ...game, state: 'picked' } : game))

		socket.emit('modifyMatch', { games: newGames })
	}

	function copyToUpcoming() {
		let newGames = [...match.games]

		// find first upcoming game
		const upcomingIndex = newGames.findIndex((g) => g && g.state === 'upcoming')
		if (upcomingIndex === -1) return // nothing to copy to

		if (!gameCivDraftView || !gameCivDraftView.host || !gameCivDraftView.guest) return

		const leftGameCivDraft = gameCivDraftView.host
			.filter((d) => d.action !== 'snipe')
			.map((d) => ({
				civ: d.option,
				action: d.action,
				result: d.result,
			}))
		const rightGameCivDraft = gameCivDraftView.guest
			.filter((d) => d.action !== 'snipe')
			.map((d) => ({
				civ: d.option,
				action: d.action,
				result: d.result,
			}))

		leftGameCivDraft.forEach((element) => {
			if (element.result === 'sniped') element.action = 'sniped'
		})

		rightGameCivDraft.forEach((element) => {
			if (element.result === 'sniped') element.action = 'sniped'
		})

		const leftCivs = leftGameCivDraft.filter((v) => v.action === 'pick').map((v) => v.civ)
		const rightCivs = rightGameCivDraft.filter((v) => v.action === 'pick').map((v) => v.civ)

		newGames[upcomingIndex] = {
			...newGames[upcomingIndex],
			leftCivs,
			rightCivs,
			leftGameCivDraft,
			rightGameCivDraft,
		}

		socket.emit('modifyMatch', { games: newGames })
	}

	return (
		<>
			<div className="flex flex-row pt-4">
				{match?.games && (
					<div className="flex flex-col">
						<div className="flex flex-row">
							<form className="flex flex-col" onSubmit={handleRoundInfo}>
								<label>Round Info</label>
								<input type="text" id="roundInfo" name="roundInfo" className="w-56 mr-8" defaultValue={match.roundInfo} />
								<label className="mt-2">Match Title</label>
								{/* one field: type freely, or use the arrow to pick a preset. The select sits
								    transparently over the arrow so the picker opens on click in every browser */}
								<div className="relative w-56 mr-8">
									<input
										type="text"
										id="matchTitle"
										name="matchTitle"
										list="matchTitlePresets"
										autoComplete="off"
										className="w-full pr-7"
										value={matchTitle}
										onChange={(el) => setMatchTitle(el.target.value)}
									/>
									<datalist id="matchTitlePresets">
										{MATCH_TITLE_PRESETS.map((option) => (
											<option key={option.id} value={option.label} />
										))}
									</datalist>
									{/* the input keeps its white background in dark mode, so the arrow needs an
									    explicit dark colour instead of inheriting the light dashboard text colour */}
									<FaChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-600" />
									<select
										aria-label="Match title presets"
										value={findMatchTitlePreset(matchTitle)?.id ?? ''}
										onChange={handleMatchTitlePresetChange}
										className="absolute inset-y-0 right-0 w-7 cursor-pointer opacity-0">
										<option value="">Custom / clear</option>
										{MATCH_TITLE_PRESETS.map((option) => (
											<option key={option.id} value={option.id}>
												{option.label}
											</option>
										))}
									</select>
								</div>
								<button className="w-24 mt-1" type="submit">
									Submit
								</button>
							</form>
							<div className="flex flex-col">
								<label>Status</label>
								<input type="text" id="draftUrl" name="draftUrl" className="w-56 mr-8" value={match.matchStatus} />
								<select
									value={statusPresets.find((item) => item.value === match.matchStatus).id}
									onChange={handleMatchStatusChange}
									className="border-solid border-[0.5px] h-8 w-56">
									{statusPresets.map((option) => (
										<option value={option.id}>{option.value}</option>
									))}
								</select>
							</div>
							<div className="flex flex-col gap-2">
								<label className="font-semibold text-sm">Timer</label>
								<div className="text-3xl font-mono tracking-wider text-center py-1">
									{match.countdownTimer ? <Countdown date={match.countdownTimer} renderer={countdownRenderer} /> : <span className="opacity-40">00:00</span>}
								</div>

								{/* Presets */}
								<div className="flex flex-wrap gap-1">
									{[0, 1, 2, 3, 4, 5].map((min) => (
										<button
											key={min}
											onClick={() => getFutureTimestamp(min * 60)}
											className="flex-1 min-w-[3rem] h-8 p-0 text-xs bg-green-600 hover:bg-green-700 rounded">
											{min} min
										</button>
									))}
									<button onClick={() => getFutureTimestamp(null)} className="flex-1 min-w-[3rem] h-8 p-0 text-xs bg-red-600 hover:bg-red-700 rounded">
										HIDE
									</button>
								</div>

								{/* Custom input */}
								<div className="flex items-center gap-1">
									<input
										type="number"
										min="0"
										max="59"
										value={customMinutes}
										onChange={(e) => setCustomMinutes(e.target.value)}
										className="w-14 h-8 text-center rounded border border-gray-500"
										placeholder="mm"
									/>
									<span className="font-bold">:</span>
									<input
										type="number"
										min="0"
										max="59"
										value={customSeconds}
										onChange={(e) => setCustomSeconds(e.target.value)}
										className="w-14 h-8 text-center rounded border border-gray-500"
										placeholder="ss"
									/>
									<button onClick={setCustomTimer} className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 rounded">
										Set
									</button>
								</div>

								{/* Live adjust */}
								<div className="flex gap-1">
									<button onClick={() => adjustTimer(-10)} className="flex-1 h-8 p-0 text-xs bg-orange-600 hover:bg-orange-700 rounded">
										-10s
									</button>
									<button onClick={() => adjustTimer(-5)} className="flex-1 h-8 p-0 text-xs bg-orange-600 hover:bg-orange-700 rounded">
										-5s
									</button>
									<button onClick={() => adjustTimer(5)} className="flex-1 h-8 p-0 text-xs bg-teal-600 hover:bg-teal-700 rounded">
										+5s
									</button>
									<button onClick={() => adjustTimer(10)} className="flex-1 h-8 p-0 text-xs bg-teal-600 hover:bg-teal-700 rounded">
										+10s
									</button>
								</div>
							</div>
						</div>

						<div className="flex flex-row">
							<div className="flex flex-col">
								<label>Map Draft</label>
								<div className="flex flex-row">
									<input type="text" id="mapDraftUrl" name="mapDraftUrl" className="w-56 mr-1" value={mapDraftUrl} onChange={handleMapDraftUrlChange} />
									<a
										href={`https://aoe2cm.net/draft/${mapDraftUrl}`}
										target="_blank"
										rel="noreferrer"
										className={`mr-4 h-8 flex items-center px-2 rounded bg-teal-700 hover:bg-teal-800 text-white ${mapDraftUrl ? '' : 'pointer-events-none opacity-40'}`}
										title="Open map draft in new tab">
										<FaExternalLinkAlt />
									</a>
									<button className="h-8 my-auto mr-2" onClick={handleImportMapDraft}>
										Import/Connect
									</button>
									<button className="h-8 my-auto mr-4 w-28 text-xs" onClick={copyNamesFromMapDraft}>
										Copy Names
									</button>
								</div>
								<label>Civ Draft</label>
								<div className="flex flex-row">
									<input type="text" id="civDraftUrl" name="civDraftUrl" className="w-56 mr-1" value={civDraftUrl} onChange={handleCivDraftUrlChange} />
									<a
										href={`https://aoe2cm.net/draft/${civDraftUrl}`}
										target="_blank"
										rel="noreferrer"
										className={`mr-4 h-8 flex items-center px-2 rounded bg-teal-700 hover:bg-teal-800 text-white ${civDraftUrl ? '' : 'pointer-events-none opacity-40'}`}
										title="Open civ draft in new tab">
										<FaExternalLinkAlt />
									</a>
									<button className="h-8 my-auto mr-2 " onClick={handleImportCivDraft}>
										Import/Connect
									</button>
									<button className="h-8 my-auto mr-4 w-28 text-xs" onClick={copyNamesFromCivDraft}>
										Copy Names
									</button>
								</div>
								{!!usePhysicalDraft && (
									<>
										<label>Civ Preset</label>
										<div className="flex flex-row civPreset">
											<input type="text" id="presetUrl" name="presetUrl" className="w-56 mr-1" value={presetUrl} onChange={handlePresetUrlChange} />
											<a
												href={`https://aoe2cm.net/preset/${presetUrl}`}
												target="_blank"
												rel="noreferrer"
												className={`mr-4 h-8 flex items-center px-2 rounded bg-teal-700 hover:bg-teal-800 text-white ${presetUrl ? '' : 'pointer-events-none opacity-40'}`}
												title="Open civ preset in new tab">
												<FaExternalLinkAlt />
											</a>
											<button className="h-8 my-auto mr-2 " onClick={handleImportPreset}>
												Get Preset
											</button>
										</div>
									</>
								)}
								<label>Game Civ Draft</label>
								<div className="flex flex-row">
									<input
										type="text"
										id="gameCivDraftUrl"
										name="gameCivDraftUrl"
										className="w-56 mr-1"
										value={gameCivDraftUrl}
										onChange={handleGameCivDraftUrlChange}
									/>
									<a
										href={`https://aoe2cm.net/draft/${gameCivDraftUrl}`}
										target="_blank"
										rel="noreferrer"
										className={`mr-4 h-8 flex items-center px-2 rounded bg-teal-700 hover:bg-teal-800 text-white ${gameCivDraftUrl ? '' : 'pointer-events-none opacity-40'}`}
										title="Open game civ draft in new tab">
										<FaExternalLinkAlt />
									</a>
									<button className="h-8 my-auto mr-2 " onClick={handleImportGameCivDraft}>
										Import/Connect
									</button>
									<button className="h-8 my-auto mr-4 w-28 text-xs" onClick={copyNamesFromGameCivDraft}>
										Copy Names
									</button>
								</div>
							</div>
							<div className="flex flex-col"></div>
							<div className="flex flex-col">
								<label>Format</label>
								<select value={match.matchType} className="border-solid border-[0.5px] h-8 w-28" onChange={handleMatchTypeChange}>
									{/* Keep the current value selectable while the list loads, or if it is not in the store */}
									{match.matchType && !gameOptions.some((option) => option.key === match.matchType) && <option value={match.matchType}>{match.matchType}</option>}
									{gameOptions.map((option) => (
										<option key={option.key} value={option.key}>
											{option.key}
										</option>
									))}
								</select>
							</div>
							{(hasRedraft || !!match.matchRedraftAfter) && (
								<div className="flex flex-col">
									<label>Redraft Offset</label>
									<input
										type="number"
										id="matchRedraftAfter"
										name="matchRedraftAfter"
										value={match.matchRedraftAfter || 0}
										className="border-solid border-[0.5px] h-8 w-16"
										onChange={handleMatchRedraftAfterChange}
									/>
								</div>
							)}
						</div>
						{gameCivDraftView?.host && (
							<div className="mt-8 w-[600px]">
								<button className="h-6 py-0 my-auto float-right" onClick={copyToUpcoming}>
									Copy to Upcoming
								</button>
								<h3 className="ml-2 mb-1 font-bold">Game Civ Draft:</h3>
								<div className="flex flex-row">
									<div className="px-2 border-black" style={{ borderRightWidth: '3px' }}>
										<div className="pb-2">
											Name: {gameCivDraftView.hostName}
											<button className="h-6 py-0 my-auto float-right" onClick={handleSwapGameCivDraftHost}>
												Swap
											</button>
										</div>
										{gameCivDraftView.host.map((draftOption) => (
											<CivSelector
												civ={draftOption.option}
												state={draftOption.action}
												leftSide={true}
												allowEmpty={true}
												sniped={draftOption.result === 'sniped'}
											/>
										))}
										{/* <div className='flex flex-row justify-center'>
                                        <button className='bg-green-500'>+</button>
                                        </div> */}
									</div>

									<div className="px-2">
										<div className="pb-2">Name: {gameCivDraftView.guestName}</div>
										{gameCivDraftView.guest.map((draftOption) => (
											<CivSelector
												civ={draftOption.option}
												state={draftOption.action}
												leftSide={false}
												allowEmpty={true}
												sniped={draftOption.result === 'sniped'}
											/>
										))}
									</div>
								</div>
							</div>
						)}
						{civDraftView?.host && (
							<div className="mt-8">
								<h3 className="ml-2 mb-1 font-bold">Match Civ Draft:</h3>
								<div className="flex flex-row">
									<div className="px-2 border-black" style={{ borderRightWidth: '3px' }}>
										<div className="pb-2">
											Name: {civDraftView.hostName}
											<button className="h-6 py-0 my-auto float-right" onClick={handleSwapCivDraftHost}>
												Swap
											</button>
										</div>
										{civDraftView.host.map((draftOption, i) => (
											<CivSelector
												civ={draftOption.option}
												state={draftOption.action}
												leftSide={true}
												allowEmpty={true}
												index={i}
												onChange={handleChangeCiv}
												sniped={draftOption.result === 'sniped'}
											/>
										))}
										{/* <div className='flex flex-row justify-center'>
                                        <button className='bg-green-500'>+</button>
                                        </div> */}
									</div>

									<div className="px-2">
										<div className="pb-2">Name: {civDraftView.guestName}</div>
										{civDraftView.guest.map((draftOption, i) => (
											<CivSelector
												civ={draftOption.option}
												state={draftOption.action}
												leftSide={false}
												allowEmpty={true}
												index={i}
												onChange={handleChangeCiv}
												sniped={draftOption.result === 'sniped'}
											/>
										))}
									</div>
								</div>
							</div>
						)}
						{mapDraftView?.host && (
							<div className="mt-8">
								<h3 className="ml-2 mb-1 font-bold">Match Map Draft:</h3>
								<div className="flex flex-row">
									<div className="px-2 border-black" style={{ borderRightWidth: '3px' }}>
										<div className="pb-2">
											Name: {mapDraftView.hostName}
											<button className="h-6 py-0 my-auto float-right" onClick={handleSwapMapDraftHost}>
												Swap
											</button>
										</div>
										{mapDraftView.host.map((draftOption, index) => (
											<MapSelector
												map={draftOption.option}
												state={draftOption.action}
												leftSide={true}
												allowEmpty={true}
												maps={effectiveMaps}
												iconMode="small"
												onChange={handleChangeMap}
												index={index}
											/>
										))}
										{/* <div className='flex flex-row justify-center'>
                                        <button className='bg-green-500'>+</button>
                                        </div> */}
									</div>

									<div className="px-2">
										<div className="pb-2">Name: {mapDraftView.guestName}</div>
										{mapDraftView.guest.map((draftOption, index) => (
											<MapSelector
												map={draftOption.option}
												state={draftOption.action}
												leftSide={false}
												allowEmpty={true}
												maps={effectiveMaps}
												iconMode="small"
												onChange={handleChangeMap}
												index={index}
											/>
										))}
									</div>
								</div>
							</div>
						)}
					</div>
				)}

				{match && (
					<div className="flex flex-col mt-20 ml-8">
						<div className="flex flex-row">
							<button className="h-6 py-0 mx-auto w-56" onClick={convertFromMapDraft}>
								Convert from Map Draft
							</button>
							<button className="h-6 py-0 mx-auto w-56" onClick={setAllMapsPicked}>
								Set all to picked
							</button>
						</div>
						<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGameDragEnd}>
							<SortableContext items={match.games.map((_, i) => gameSortId(i))} strategy={verticalListSortingStrategy}>
								{match?.games.map((game, gameIndex) => (
									<SortableGame key={gameSortId(gameIndex)} id={gameSortId(gameIndex)} gameNumber={gameIndex + 1}>
										<div className="flex flex-col">
											{match.matchType.includes('2v2') ? (
												<>
													<CivSelector
														key={`${gameIndex}0`}
														civ={game.leftCivs[0]}
														leftSide={true}
														allowEmpty={true}
														availableCivs={leftAvailableCivs}
														onChange={(civ) =>
															modifyGame(gameIndex, {
																leftCivs: [civ, game.leftCivs[1]],
															})
														}
													/>
													<CivSelector
														key={`${gameIndex}1`}
														civ={game.leftCivs[1]}
														leftSide={true}
														allowEmpty={true}
														availableCivs={leftAvailableCivs}
														onChange={(civ) =>
															modifyGame(gameIndex, {
																leftCivs: [game.leftCivs[0], civ],
															})
														}
													/>
												</>
											) : match.matchType.includes('ABBA') ? (
												<>
													<CivSelector
														key={`${gameIndex}0`}
														civ={game?.leftGameCivDraft?.[0]?.civ}
														state={game?.leftGameCivDraft?.[0]?.action}
														leftSide={true}
														allowEmpty={true}
														availableCivs={leftAvailableCivs}
														index={0}
														showDraftOptions
														sniped={game?.leftGameCivDraft?.[0]?.result === 'sniped'}
													/>
													<CivSelector
														key={`${gameIndex}1`}
														civ={game?.leftGameCivDraft?.[1]?.civ}
														state={game?.leftGameCivDraft?.[1]?.action}
														leftSide={true}
														allowEmpty={true}
														availableCivs={leftAvailableCivs}
														index={1}
														showDraftOptions
														sniped={game?.leftGameCivDraft?.[1]?.result === 'sniped'}
													/>
												</>
											) : match.matchType.includes('3v3') ? (
												<>
													<CivSelector
														key={`${gameIndex}0`}
														civ={game.leftCivs[0]}
														leftSide={true}
														allowEmpty={true}
														availableCivs={leftAvailableCivs}
														onChange={(civ) =>
															modifyGame(gameIndex, {
																leftCivs: [civ, game.leftCivs[1], game.leftCivs[2]],
															})
														}
													/>
													<CivSelector
														key={`${gameIndex}1`}
														civ={game.leftCivs[1]}
														leftSide={true}
														allowEmpty={true}
														availableCivs={leftAvailableCivs}
														onChange={(civ) =>
															modifyGame(gameIndex, {
																leftCivs: [game.leftCivs[0], civ, game.leftCivs[2]],
															})
														}
													/>
													<CivSelector
														key={`${gameIndex}2`}
														civ={game.leftCivs[2]}
														leftSide={true}
														allowEmpty={true}
														availableCivs={leftAvailableCivs}
														onChange={(civ) =>
															modifyGame(gameIndex, {
																leftCivs: [game.leftCivs[0], game.leftCivs[1], civ],
															})
														}
													/>
												</>
											) : (
												<CivSelector
													civ={game.leftCivs[0]}
													leftSide={true}
													allowEmpty={true}
													availableCivs={leftAvailableCivs}
													onChange={(civ) =>
														modifyGame(gameIndex, {
															leftCivs: [civ],
														})
													}
												/>
											)}
										</div>
										<div className="flex flex-col">
											<MapSelector map={game.map} allowEmpty={true} onChange={(map) => modifyGame(gameIndex, { map: map })} maps={effectiveMaps} />
											<select
												value={game.state}
												className="border-solid border-[0.5px] h-8 w-24 mx-auto"
												onChange={(el) =>
													modifyGame(gameIndex, {
														state: el.target.value,
													})
												}>
												{gameStateOptions.map((option) => (
													<option value={option}>{option}</option>
												))}
											</select>
										</div>
										<div className="flex flex-col">
											{match.matchType.includes('2v2') ? (
												<>
													<CivSelector
														civ={game.rightCivs[0]}
														leftSide={false}
														allowEmpty={true}
														availableCivs={rightAvailableCivs}
														onChange={(civ) =>
															modifyGame(gameIndex, {
																rightCivs: [civ, game.rightCivs[1]],
															})
														}
													/>
													<CivSelector
														civ={game.rightCivs[1]}
														leftSide={false}
														allowEmpty={true}
														availableCivs={rightAvailableCivs}
														onChange={(civ) =>
															modifyGame(gameIndex, {
																rightCivs: [game.rightCivs[0], civ],
															})
														}
													/>
												</>
											) : match.matchType.includes('ABBA') ? (
												<>
													<CivSelector
														civ={game?.rightGameCivDraft?.[0]?.civ}
														state={game?.rightGameCivDraft?.[0]?.action}
														leftSide={false}
														allowEmpty={true}
														availableCivs={rightAvailableCivs}
														index={0}
														showDraftOptions
														sniped={game?.rightGameCivDraft?.[0]?.result === 'sniped'}
													/>
													<CivSelector
														civ={game?.rightGameCivDraft?.[1]?.civ}
														state={game?.rightGameCivDraft?.[1]?.action}
														leftSide={false}
														allowEmpty={true}
														availableCivs={rightAvailableCivs}
														index={1}
														showDraftOptions
														sniped={game?.rightGameCivDraft?.[1]?.result === 'sniped'}
													/>
												</>
											) : match.matchType.includes('3v3') ? (
												<>
													<CivSelector
														civ={game.rightCivs[0]}
														leftSide={false}
														allowEmpty={true}
														availableCivs={rightAvailableCivs}
														onChange={(civ) =>
															modifyGame(gameIndex, {
																rightCivs: [civ, game.rightCivs[1], game.rightCivs[2]],
															})
														}
													/>
													<CivSelector
														civ={game.rightCivs[1]}
														leftSide={false}
														allowEmpty={true}
														availableCivs={rightAvailableCivs}
														onChange={(civ) =>
															modifyGame(gameIndex, {
																rightCivs: [game.rightCivs[0], civ, game.rightCivs[2]],
															})
														}
													/>
													<CivSelector
														civ={game.rightCivs[2]}
														leftSide={false}
														allowEmpty={true}
														availableCivs={rightAvailableCivs}
														onChange={(civ) =>
															modifyGame(gameIndex, {
																rightCivs: [game.rightCivs[0], game.rightCivs[1], civ],
															})
														}
													/>
												</>
											) : (
												<CivSelector
													civ={game.rightCivs[0]}
													leftSide={false}
													allowEmpty={true}
													availableCivs={rightAvailableCivs}
													onChange={(civ) =>
														modifyGame(gameIndex, {
															rightCivs: [civ],
														})
													}
												/>
											)}
										</div>
									</SortableGame>
								))}
							</SortableContext>
						</DndContext>
						{/* <div className='flex flex-row justify-center pt-4'>
                            <button className='bg-green-500 hover:bg-green-700'>+</button>
                        </div> */}
					</div>
				)}
			</div>
		</>
	)
}

export default DraftDashboard
